import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { CaptureIcon, EyeIcon, LoopIcon } from './icons';
import './styles.scss';

const systems = [
  {
    icon: CaptureIcon,
    label: 'System · 01',
    title: 'Capture the field',
    desc:
      'Mobile-first, offline-ready capture designed for the slab, the scaffold, and the trailer. Two minutes a day.',
    chips: [
      { label: 'Daily Logs', to: '/features/daily_log' },
      { label: 'Punch Lists', to: '/features/punchlists' },
      { label: 'Forms', to: '/features/forms' },
      { label: 'Time Tracking', to: '/features/time_tracking' },
    ],
  },
  {
    icon: EyeIcon,
    label: 'System · 02',
    title: 'See the whole job',
    desc:
      'Every plan, place, and tool visible to the people who need it, when they need it. Version-controlled, QR-coded, always current.',
    chips: [
      { label: 'Drawings', to: '/features/drawings' },
      { label: 'Locations & QR', to: '/features/locations' },
      { label: 'Asset Tracking', to: '/features/asset_tracking' },
    ],
  },
  {
    icon: LoopIcon,
    label: 'System · 03',
    title: 'Close every loop',
    desc:
      'Every question, milestone, and to-do tracked in one place with a stamped paper trail.',
    chips: [
      { label: 'RFIs', to: '/features/rfi' },
      { label: 'Submittals', to: '/features/submittals' },
      { label: 'Action Items', to: '/features/actions' },
    ],
  },
];

const problems = [
  {
    tone: 'orange',
    text:
      'Plans in one app, RFIs in email, daily logs on paper. The truth of the job scatters across ten places.',
  },
  {
    tone: 'blue',
    text: 'Legacy platforms charge 1–3% of your annual construction volume to un-scatter it.',
  },
  {
    tone: 'green',
    text:
      'Subs stay off systems that bill them to participate — so the record has holes exactly where the risk is.',
  },
];

export default function PlatformSection() {
  return (
    <section className="plh-platform" id="platform">
      <div className="plh-platform__head">
        <Reveal className="plh-platform__headline">
          <p className="plh-eyebrow">The platform</p>
          <h2>
            <span className="plh-chapter">01 —</span> Every tool your jobsite needs, wired into{' '}
            <span className="plh-accent">one record.</span>
          </h2>
        </Reveal>
        <Reveal as="ul" className="plh-platform__problems" delay={120}>
          {problems.map((problem) => (
            <li className={`plh-problem plh-problem--${problem.tone}`} key={problem.tone}>
              {problem.text}
            </li>
          ))}
        </Reveal>
      </div>

      <div className="plh-systems">
        {systems.map((system, idx) => {
          const Icon = system.icon;
          return (
            <Reveal className="plh-system" key={system.title} delay={idx * 90}>
              <span className="plh-system__icon" aria-hidden="true">
                <Icon />
              </span>
              <div className="plh-system__text">
                <p className="plh-system__label plh-mono">{system.label}</p>
                <h3>{system.title}</h3>
                <p>{system.desc}</p>
              </div>
              <div className="plh-system__chips">
                {system.chips.map((chip) => (
                  <Link className="plh-chip" to={chip.to} key={chip.label}>
                    {chip.label} <span aria-hidden="true">→</span>
                  </Link>
                ))}
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal className="plh-platform__banner" delay={120}>
        <p>Eleven tools writing one connected record — from precon to closeout.</p>
        <Link to="/features" className="pl-btn pl-btn--primary">
          Explore the complete platform <span aria-hidden="true">→</span>
        </Link>
      </Reveal>
    </section>
  );
}
